import { inject, Injectable } from '@angular/core'
import type { Observable } from 'rxjs'
import { catchError, defer, EMPTY, map, tap, throwError } from 'rxjs'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { apiEndpoint } from '@awork/environments/environment'
import type { OnboardingTaskKey } from '@awork/features/onboarding/models/types'
import { OnboardingTaskProgress } from '../state/onboarding-task-progress.model'
import { OnboardingTaskProgressStore } from '../state/onboarding-task-progress.store'
import { OnboardingTaskProgressQuery } from '../state/onboarding-task-progress.query'
import { OnboardingLeaderboard } from '../state/onboarding-leaderboard.model'
import { OnboardingLeaderboardStore } from '../state/onboarding-leaderboard.store'

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private apiClient = inject(ApiClient)
  private taskProgressStore = inject(OnboardingTaskProgressStore)
  private taskProgressQuery = inject(OnboardingTaskProgressQuery)
  private leaderboardStore = inject(OnboardingLeaderboardStore)

  private readonly taskProgressUrl = `${apiEndpoint}/me/onboarding/tasks`
  private readonly leaderboardUrl = `${apiEndpoint}/me/onboarding/leaderboard`

  /**
   * Fetches all visible onboarding tasks with their progress for the current user
   * Backend determines visibility and order based on user's subscription/role
   * @returns {Observable<OnboardingTaskProgress[]>}
   */
  fetchTaskProgress(): Observable<OnboardingTaskProgress[]> {
    return defer(() => {
      this.taskProgressStore.setLoading(true)

      return this.apiClient.getAll<OnboardingTaskProgress[]>(this.taskProgressUrl).pipe(
        map(tasks => tasks.map(task => new OnboardingTaskProgress(task))),
        tap(tasks => {
          this.taskProgressStore.set(tasks)
          this.taskProgressStore.setLoading(false)
        }),
        catchError(error => {
          this.taskProgressStore.setLoading(false)
          return throwError(() => error)
        })
      )
    })
  }

  /**
   * Updates task completion status with optimistic update and rollback on error
   * @param {OnboardingTaskKey} taskKey
   * @param {boolean} isDone
   * @returns {Observable<OnboardingTaskProgress>}
   */
  updateTask(taskKey: OnboardingTaskKey, isDone: boolean): Observable<OnboardingTaskProgress> {
    const taskProgressSignal = this.taskProgressQuery.queryTaskProgressByKey(taskKey)
    const taskProgress = taskProgressSignal()

    if (!taskProgress) {
      return EMPTY
    }

    const operationId = this.taskProgressStore.history.startOperation({
      type: 'update',
      entities: [taskProgress]
    })

    // Optimistic update
    this.taskProgressStore.update(taskProgress.id, {
      isDone,
      completedOn: isDone ? new Date() : undefined
    })

    return this.apiClient.put<OnboardingTaskProgress>(`${this.taskProgressUrl}/${taskProgress.id}`, { isDone }).pipe(
      map(progress => new OnboardingTaskProgress(progress)),
      tap(progress => {
        this.taskProgressStore.update(progress.id, progress)
        this.taskProgressStore.history.endOperation(operationId)
      }),
      catchError(error => {
        this.taskProgressStore.history.undo(operationId)
        return throwError(() => error)
      })
    )
  }

  /**
   * Fetches the onboarding leaderboard data for the workspace
   * Backend determines visibility based on user's permissions
   * @returns {Observable<OnboardingLeaderboard[]>}
   */
  fetchLeaderboard(): Observable<OnboardingLeaderboard[]> {
    return this.apiClient.getAll<OnboardingLeaderboard[]>(this.leaderboardUrl).pipe(
      map(entries => entries.map(entry => new OnboardingLeaderboard(entry))),
      tap(entries => {
        this.leaderboardStore.set(entries)
        this.leaderboardStore.setLoading(false)
      }),
      catchError(error => {
        this.leaderboardStore.setLoading(false)
        return throwError(() => error)
      })
    )
  }
}
